import React, { useState } from "react"
import SkeletonParagraph from "../SkeletonParagraph"

const Reviews = ({ appdata }) => {
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const reviewsPerPage = 2

  const ratingData = appdata?.rating
  const istotalratingAvailable = ratingData?.total_rating || false
  var overallRating = 0.0
  if (istotalratingAvailable && ratingData.rating_index) {
    overallRating = (
      ((ratingData.rating_index["1"] || 0) +
        (ratingData.rating_index["2"] || 0) * 2 +
        (ratingData.rating_index["3"] || 0) * 3 +
        (ratingData.rating_index["4"] || 0) * 4 +
        (ratingData.rating_index["5"] || 0) * 5) /
      ratingData.total_rating
    ).toFixed(1)
  }

  const StarIcon = ({ size = 16, color = '#FFD700' }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 640 640"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline-block' }}
    >
      <path
        fill={color}
        d="M341.5 45.1C337.4 37.1 329.1 32 320.1 32C311.1 32 302.8 37.1 298.7 45.1L225.1 189.3L65.2 214.7C56.3 216.1 48.9 222.4 46.1 231C43.3 239.6 45.6 249 51.9 255.4L166.3 369.9L141.1 529.8C139.7 538.7 143.4 547.7 150.7 553C158 558.3 167.6 559.1 175.7 555L320.1 481.6L464.4 555C472.4 559.1 482.1 558.3 489.4 553C496.7 547.7 500.4 538.8 499 529.8L473.7 369.9L588.1 255.4C594.5 249 596.7 239.6 593.9 231C591.1 222.4 583.8 216.1 574.8 214.7L415 189.3L341.5 45.1z"
      />
    </svg>
  )

  const renderStars = (rating, size = 16, gap = 4) => {
    const stars = []
    const safeRating = Math.max(0, Math.min(5, Math.round(rating)))
    for (let i = 1; i <= safeRating; i++) {
      stars.push(
        <span key={i} style={{ marginRight: gap, display: 'inline-flex' }}>
          <StarIcon size={size} />
        </span>
      )
    }
    return stars
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear().toString().slice(-2)
    return `${day}-${month}-${year}`
  }

  const handleViewMoreReviews = async () => {
    if (!showAllReviews) {
      setShowAllReviews(true)
      setCurrentPage(1)
    } else {
      setIsLoading(true)
      // Simulate API call delay
      setTimeout(() => {
        setCurrentPage(prev => prev + 1)
        setIsLoading(false)
      }, 1000)
    }
  }

  const handleViewLessReviews = () => {
    setShowAllReviews(false)
    setCurrentPage(1)
  }

  const allReviews = appdata?.reviews || []
  const displayedReviews = showAllReviews
    ? allReviews.slice(0, currentPage * reviewsPerPage)
    : allReviews.slice(0, 2)

  const hasMoreReviews = showAllReviews && displayedReviews.length < allReviews.length
  const showViewLess = showAllReviews && displayedReviews.length >= allReviews.length
  const showViewMore = (!showAllReviews && allReviews.length > 2) || (showAllReviews && hasMoreReviews)

  return (
    <div className="reviews_section">
      <div className="row">
        {/* Left Column - Featured Reviews */}
        <div className="col-lg-8">
          <div className="featured_reviews">
            <h4 className="reviews_title">Featured Reviews</h4>
            <div className="reviews_list">
              {displayedReviews.map((comment, k) => (
                <div key={"review_" + k} className="review_item">
                  <div className="review_header">
                    <div className="review_lt">
                      <div className="review_title">
                        {k + 1}. {comment.title || "Great app!"}
                      </div>
                      <div className="review_stars">
                        {renderStars(comment.rating || 5, 16, 4)}
                      </div>
                    </div>
                    <div className="review_rt">
                      <div className="meta_date">{formatDate(comment.created_at)}</div>
                    </div>
                  </div>
                  <div className="review_meta">
                    <span className="meta_item">• By {comment.name || "User"}</span>
                    <span className="meta_item">• Version {appdata?.version || "1.0"}</span>

                  </div>
                  <div className="review_content">
                    <p>{comment.review}</p>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="review_item">
                  <SkeletonParagraph />
                </div>
              )}
            </div>

            {showViewMore && (
              <div className="view_more_section">
                <button
                  className="view_more_btn"
                  onClick={handleViewMoreReviews}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "View More Reviews"}
                </button>
              </div>
            )}

            {showViewLess && (
              <div className="view_more_section">
                <button className="view_more_btn" onClick={handleViewLessReviews}>
                  View Less Reviews
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Rating Summary */}
        <div className="col-lg-4">
          <div className="rating_summary">
            <h4 className="reviews_title">Average Rating</h4>
            <div className="average_rating">
              <div className="stars_large">
                {renderStars(Math.round(overallRating), 22, 6)}
              </div>
              <div className="total_ratings">
                {ratingData?.total_rating?.toLocaleString() || "0"}
              </div>
            </div>

            <h4 className="reviews_title mt-4">Rating Breakdown</h4>
            <div className="rating_breakdown">
              {[5, 4, 3, 2, 1].map((stars) => (
                <div key={stars} className="rating_row">
                  <div className="rating_stars">
                    {renderStars(stars, 16, 4)}
                  </div>
                  <div className="rating_count">
                    {ratingData?.rating_index?.[stars]?.toLocaleString() || "0"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Reviews
